import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import type { BrandRow } from './client-dialog-form';

interface ClientBrandsFieldProps {
  rows: BrandRow[];
  error: string | undefined;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, value: string) => void;
}

export function ClientBrandsField({
  rows,
  error,
  onAdd,
  onRemove,
  onUpdate,
}: ClientBrandsFieldProps) {
  const { t } = useTranslation('clients');

  return (
    <div className="space-y-3" data-testid="client-brands-field">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground">{t('dialog.fields.brands')}</p>
          <p className="text-xs text-muted-foreground">{t('dialog.fields.brandsHint')}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="client-add-brand-button"
          onClick={onAdd}
        >
          {t('dialog.fields.addBrand')}
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('dialog.fields.noBrands')}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <Input
                placeholder={t('dialog.fields.brandPlaceholder')}
                data-testid={`client-brand-input-${index}`}
                value={field.value}
                onChange={(event) => onUpdate(index, event.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={`client-brand-remove-${index}`}
                onClick={() => onRemove(index)}
              >
                {t('dialog.fields.removeBrand')}
              </Button>
            </div>
          ))}
        </div>
      )}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
